import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { Box, Chip, Menu } from "@mui/material";
import pluralize from "pluralize";
import React, { useState } from "react";

import { Typography } from "new-components/typography/Typography";
import { DetectorModes } from "types/createDetector";
import { Detector } from "types/detectors";
import { ANSWER_VALUES } from "types/imageQueries";

import { FilterParams } from "./FiltersData";
import { FilterChanges, GridFilters } from "./GridFilters";

export interface FilterChipProps {
  detector?: Detector;
  columnField: string;
  filterValue: string[];
  filterOperator: string | null;
  filterState: Record<string, any>;
  handleClickRemoveFilter: () => void;
  onFilterChange: (filterValues: FilterChanges[]) => void;
  filterableElements: FilterParams[];
}

const opOptionsForGT = {
  true: "Is missing",
  false: "Is present",
};

export function FilterChip(props: FilterChipProps) {
  const {
    detector,
    columnField,
    filterValue,
    filterOperator,
    filterState,
    handleClickRemoveFilter,
    onFilterChange,
    filterableElements,
  } = props;

  // Menu Filters
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClose = () => setAnchorEl(null);

  const handleClickOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const filterElement = filterableElements.find((p) => p.columnField === columnField);
  const columnFieldName = filterElement ? filterElement.headerName : columnField;

  const getFilterChipTitleValue = (filterValue: string): string => {
    const findValue =
      filterElement && filterElement.valueOptions
        ? filterElement.valueOptions.find((p) => p.value === filterValue)?.name || filterValue
        : filterValue;

    return findValue;
  };

  const getFilterChipTitleLabel = (filterValue: string): string => {
    if (detector?.mode === DetectorModes.COUNT) {
      // Creating a regex to check if the filterValue is a number or starts with the symbol >
      const isNumber = /^\d+$/;
      const isGreaterThan = /^>/;
      if (isNumber.test(filterValue) || isGreaterThan.test(filterValue)) {
        return pluralize("Object", parseInt(filterValue, 10));
      }
    }

    return "";
  };

  const getFilterChipTitle = (filterValue: string): string => {
    // eslint-disable-next-line no-underscore-dangle
    const isUnclear = [ANSWER_VALUES.__UNSURE, ANSWER_VALUES.UNCLEAR].includes(
      filterValue.toUpperCase() as ANSWER_VALUES
    );

    if (isUnclear) {
      return "Unclear";
    }

    const titleValue = getFilterChipTitleValue(filterValue);
    const titleLabel = getFilterChipTitleLabel(filterValue);

    return `${titleValue} ${titleLabel}`.trim();
  };

  return (
    <>
      <Chip
        className="bg-slate-300 text-white border-none"
        label={
          <Box className="flex gap-1">
            <Typography className="text-black">{`${columnFieldName}:`}</Typography>
            <Typography fontWeight="bold" className="text-[#0D297D]">
              {`${
                filterOperator === null
                  ? ""
                  : ` ${
                      filterOperator === "true" || filterOperator === "false"
                        ? opOptionsForGT[filterOperator]
                        : filterOperator
                    }`
              } ${
                // eslint-disable-next-line no-nested-ternary
                filterOperator === "true" || filterOperator === "false"
                  ? ""
                  : filterValue.map((value) => getFilterChipTitle(value)).join(", ")
              }`}
            </Typography>
          </Box>
        }
        deleteIcon={<HighlightOffIcon className="text-[#006DD9]" />}
        variant="outlined"
        onClick={handleClickOpen}
        onDelete={handleClickRemoveFilter}
      />
      <Menu
        id="long-menu"
        MenuListProps={{
          "aria-labelledby": "long-button",
        }}
        PaperProps={{
          style: {
            width: "auto",
            minWidth: "600px",
            maxHeight: "400px",
          },
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <GridFilters
          detector={detector}
          isEdit
          filterState={{
            filterModel: {
              items: [...filterState.filterModel.items],
            },
          }}
          currentFilter={[
            {
              id: "1",
              columnField,
              operatorValue: filterOperator,
              value: filterValue,
            },
          ]}
          onFilterChange={onFilterChange}
          handleClose={handleClose}
          filterableElements={filterableElements}
        />
      </Menu>
    </>
  );
}
